import { DayOfWeek, Location } from "@/types/collections"

/**
 * Minutes between two "HH:mm" times. An end earlier than the start is read as the next day, so an
 * overnight window still has a positive length. Missing or unparseable times give 0.
 */
export function calculateServiceTime(startTime: string, endTime: string): number {
  if (!startTime || !endTime) return 0
  const [startHour, startMin] = startTime.split(":").map(Number)
  const [endHour, endMin] = endTime.split(":").map(Number)
  if ([startHour, startMin, endHour, endMin].some((n) => Number.isNaN(n))) return 0
  const startMinutes = startHour * 60 + startMin
  let endMinutes = endHour * 60 + endMin
  if (endMinutes < startMinutes) endMinutes += 24 * 60
  return Math.max(0, endMinutes - startMinutes)
}

/**
 * The default slot name: `<location code>-<day codes>-<HHmm>`, e.g. `SAM123-MONWED-0800`.
 *
 * The location code is the site's Samsara location id, else the last four characters of its row
 * id, else "LOC" when the site is not known. Empty when there is no location, day or start time.
 */
export function calculateSlotId(
  locationId: string,
  location: Pick<Location, "id" | "location_id"> | undefined,
  days: DayOfWeek[],
  startTime: string,
): string {
  if (!locationId || !days || days.length === 0 || !startTime) return ""
  const locationCode = location?.location_id || location?.id.slice(-4).toUpperCase() || "LOC"
  const dayCodes = days.map((d) => d.substring(0, 3).toUpperCase()).join("")
  const timeCode = startTime.replace(":", "")
  return `${locationCode}-${dayCodes}-${timeCode}`
}
